import { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { ContentBlock, DocPage } from '../../types/doc';

interface DocFormProps {
  initial?: Partial<DocPage>;
  loading?: boolean;
  onSubmit: (values: Partial<DocPage>) => Promise<void> | void;
}

type DocFormValues = Partial<DocPage> & { tagsAsString?: string };

const createBlockTemplate = (type: ContentBlock['type'] = 'text'): ContentBlock => {
  switch (type) {
    case 'image':
    case 'video':
      return { type, data: { url: '', caption: '' } };
    case 'code':
      return { type, data: { language: 'bash', value: '' } };
    case 'list':
      return { type, data: { ordered: false, items: ['First item'] } };
    case 'table':
      return { type, data: { headers: ['Column 1', 'Column 2'], rows: [['Value A', 'Value B']] } };
    case 'quote':
    case 'callout':
    case 'text':
    default:
      return { type: type ?? 'text', data: { value: '' } };
  }
};

const createSectionTemplate = () => ({
  title: '',
  slug: '',
  order: 0,
  blocks: [createBlockTemplate()]
});

const blankDoc: DocFormValues = {
  title: '',
  description: '',
  category: 'Getting Started',
  navGroup: 'Overview',
  tags: [],
  tagsAsString: '',
  published: true,
  sections: [createSectionTemplate()]
};

const DocForm = ({ initial, loading, onSubmit }: DocFormProps) => {
  const { register, handleSubmit, control, reset, watch, setValue } = useForm<DocFormValues>({
    defaultValues: blankDoc
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sections'
  });
  const sections = watch('sections') || [];

  useEffect(() => {
    if (initial) {
      reset({
        ...initial,
        tagsAsString: initial.tags?.join(', ')
      });
    } else {
      reset(blankDoc);
    }
  }, [initial, reset]);

  const normalizeBlock = (block: any): ContentBlock => {
    switch (block.type) {
      case 'image':
      case 'video':
        return {
          type: block.type,
          data: {
            url: block.data?.url || '',
            caption: block.data?.caption || ''
          }
        };
      case 'code':
        return {
          type: 'code',
          data: {
            language: block.data?.language || 'bash',
            value: block.data?.value || ''
          }
        };
      case 'list':
        return {
          type: 'list',
          data: {
            ordered: Boolean(block.data?.ordered),
            items: Array.isArray(block.data?.items)
              ? block.data.items.filter((item: string) => item.length)
              : []
          }
        };
      case 'table':
        // Handle both string (during typing) and array (processed) formats for headers
        let processedHeaders: string[] = [];
        if (typeof block.data?.headers === 'string') {
          // Process string format (from typing)
          processedHeaders = block.data.headers
            .split(',')
            .map((header: string) => header.trim())
            .filter((header: string) => header.length > 0);
        } else if (Array.isArray(block.data?.headers)) {
          // Already processed array format
          processedHeaders = block.data.headers.filter((header: string) => header.length);
        }
        
        // Handle both string (during typing) and array (processed) formats for rows
        let processedRows: string[][] = [];
        if (typeof block.data?.rows === 'string') {
          // Process string format (from typing)
          const lines = block.data.rows.split('\n');
          processedRows = lines
            .map((row: string) => {
              if (row.trim().length === 0) return [];
              return row.split('|').map((cell: string) => cell.trim()).filter((cell: string) => cell.length > 0);
            })
            .filter((row: string[]) => row.length > 0);
        } else if (Array.isArray(block.data?.rows)) {
          // Already processed array format
          processedRows = block.data.rows
            .map((row: string[]) => row.filter((cell) => cell.length))
            .filter((row: string[]) => row.length);
        }
        
        return {
          type: 'table',
          data: {
            headers: processedHeaders,
            rows: processedRows
          }
        };
      case 'callout':
      case 'quote':
      case 'text':
      default:
        return {
          type: block.type || 'text',
          data: { value: block.data?.value || '' }
        };
    }
  };

  const handleFormSubmit = (values: DocFormValues) => {
    const { tagsAsString, ...rest } = values;
    const tags =
      tagsAsString?.split(',').map((tag) => tag.trim()).filter(Boolean) ||
      rest.tags ||
      [];
    
    // Ensure sections have required fields and valid blocks
    const normalizedSections =
      rest.sections?.map((section, sectionIdx) => {
        const normalizedBlocks = (section.blocks || [])
          .map((block) => normalizeBlock(block))
          .filter((block) => {
            // Filter out invalid blocks
            if (!block.type || !block.data) return false;
            // For text/quote/callout, ensure value exists
            if (['text', 'quote', 'callout'].includes(block.type)) {
              return Boolean((block.data as { value?: string }).value);
            }
            // For image/video, ensure url exists
            if (['image', 'video'].includes(block.type)) {
              return Boolean((block.data as { url?: string }).url);
            }
            // For code, ensure value exists
            if (block.type === 'code') {
              return Boolean((block.data as { value?: string }).value);
            }
            // For list, ensure items exist
            if (block.type === 'list') {
              return Array.isArray((block.data as { items?: string[] }).items) && (block.data as { items: string[] }).items.length > 0;
            }
            // For table, ensure headers and rows exist
            if (block.type === 'table') {
              const tableData = block.data as { headers?: string[] | string; rows?: string[][] | string };
              // Handle both string (during typing) and array (processed) formats for headers
              const headers = typeof tableData.headers === 'string'
                ? tableData.headers.split(',').filter(h => h.trim().length > 0)
                : (Array.isArray(tableData.headers) ? tableData.headers : []);
              
              // Handle both string (during typing) and array (processed) formats for rows
              const rows = typeof tableData.rows === 'string' 
                ? tableData.rows.split('\n').filter(row => row.trim().length > 0)
                : (Array.isArray(tableData.rows) ? tableData.rows : []);
              
              return (
                (headers.length > 0 || typeof tableData.headers === 'string') &&
                (rows.length > 0 || typeof tableData.rows === 'string')
              );
            }
            return true;
          });
        
        // Ensure at least one block exists
        if (normalizedBlocks.length === 0) {
          normalizedBlocks.push(createBlockTemplate('text'));
        }

        // Generate slug from title if not provided
        const sectionTitle = section.title || `Section ${sectionIdx + 1}`;
        const sectionSlug = section.slug || sectionTitle.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

        return {
          title: sectionTitle,
          slug: sectionSlug,
          order: sectionIdx,
          blocks: normalizedBlocks
        };
      })
      .filter((section) => section.title.trim().length > 0) || [];

    // Ensure at least one section exists
    if (normalizedSections.length === 0) {
      normalizedSections.push(createSectionTemplate());
    }

    onSubmit({
      ...rest,
      tags,
      sections: normalizedSections
    });
  };

  const addBlock = (sectionIdx: number, type: ContentBlock['type'] = 'text') => {
    const updated = [...sections];
    const nextBlocks = [...(updated[sectionIdx]?.blocks || []), createBlockTemplate(type)];
    updated[sectionIdx] = {
      ...updated[sectionIdx],
      blocks: nextBlocks
    };
    setValue('sections', updated as DocFormValues['sections']);
  };

  const removeBlock = (sectionIdx: number, blockIdx: number) => {
    const updated = [...sections];
    updated[sectionIdx].blocks?.splice(blockIdx, 1);
    if (!updated[sectionIdx].blocks?.length) {
      updated[sectionIdx].blocks = [createBlockTemplate()];
    }
    setValue('sections', updated as DocFormValues['sections']);
  };

  const handleBlockTypeChange = (sectionIdx: number, blockIdx: number, type: ContentBlock['type']) => {
    setValue(`sections.${sectionIdx}.blocks.${blockIdx}`, createBlockTemplate(type));
  };

  const renderBlockFields = (sectionIdx: number, blockIdx: number, type: ContentBlock['type']) => {
    const baseName = `sections.${sectionIdx}.blocks.${blockIdx}` as const;
    switch (type) {
      case 'image':
      case 'video':
        return (
          <div className="space-y-2">
            <input
            {...register(`${baseName}.data.url` as const)}
              placeholder={type === 'image' ? 'Image URL' : 'Video embed URL'}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
            />
            <input
            {...register(`${baseName}.data.caption` as const)}
              placeholder="Caption (optional)"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
            />
          </div>
        );
      case 'code':
        return (
          <div className="space-y-2">
            <input
              {...register(`${baseName}.data.language` as const)}
              placeholder="Language (ts, js, bash...)"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
            />
            <textarea
              {...register(`${baseName}.data.value` as const)}
              placeholder="Code snippet"
              className="h-32 w-full rounded-2xl border border-slate-200 px-3 py-2 font-mono text-sm dark:border-slate-700 dark:bg-slate-900"
            />
          </div>
        );
      case 'list':
        return (
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                {...register(`${baseName}.data.ordered` as const)}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              Ordered list
            </label>
            <Controller
              control={control}
              name={`${baseName}.data.items`}
              render={({ field }) => (
                <textarea
                  value={(field.value || []).join('\n')}
                  onChange={(event) => {
                    const lines = event.target.value.split('\n');
                    field.onChange(lines.filter((line) => line.length > 0 || lines.indexOf(line) === lines.length - 1));
                  }}
                  onBlur={() => {
                    const items = (field.value || []).filter((item: string) => item.trim().length > 0);
                    if (items.length !== (field.value || []).length) {
                      field.onChange(items);
                    }
                  }}
                  placeholder="List items (one per line)"
                  className="h-32 w-full rounded-2xl border border-slate-200 px-3 py-2 font-mono text-sm dark:border-slate-700 dark:bg-slate-900"
                />
              )}
            />
          </div>
        );
      case 'table':
        return (
          <div className="space-y-2">
            <Controller
              control={control}
              name={`${baseName}.data.headers`}
              render={({ field }) => {
                // Handle both string (during typing) and array (processed) formats
                let displayValue = '';
                if (typeof field.value === 'string') {
                  displayValue = field.value;
                } else if (Array.isArray(field.value)) {
                  displayValue = field.value.join(', ');
                }
                
                return (
                  <input
                    value={displayValue}
                    onChange={(event) => {
                      // Store raw string value - allows free typing of all characters including commas
                      field.onChange(event.target.value);
                    }}
                    onBlur={(event) => {
                      // Process the value when user finishes editing
                      const headers = event.target.value
                        .split(',')
                        .map((header) => header.trim())
                        .filter((header) => header.length > 0);
                      field.onChange(headers);
                    }}
                    placeholder="Headers separated by commas"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
                  />
                );
              }}
            />
            <Controller
              control={control}
              name={`${baseName}.data.rows`}
              render={({ field }) => {
                // Handle both string (during typing) and array (processed) formats
                let displayValue = '';
                if (typeof field.value === 'string') {
                  displayValue = field.value;
                } else if (Array.isArray(field.value)) {
                  displayValue = field.value
                    .map((row: string[]) => row.join(' | '))
                    .join('\n');
                }
                
                return (
                  <textarea
                    value={displayValue}
                    onChange={(event) => {
                      // Store raw string value - allows free typing of all characters
                      field.onChange(event.target.value);
                    }}
                    onBlur={(event) => {
                      // Process the value when user finishes editing
                      const lines = event.target.value.split('\n');
                      const rows = lines
                        .map((row) => {
                          if (row.trim().length === 0) return [];
                          return row.split('|').map((cell) => cell.trim()).filter((cell) => cell.length > 0);
                        })
                        .filter((row) => row.length > 0);
                      field.onChange(rows);
                    }}
                    placeholder="Rows (use | to separate columns, one row per line)"
                    className="h-32 w-full rounded-2xl border border-slate-200 px-3 py-2 font-mono text-sm dark:border-slate-700 dark:bg-slate-900"
                  />
                );
              }}
            />
          </div>
        );
      case 'callout':
      case 'quote':
      case 'text':
      default:
        return (
          <textarea
            {...register(`${baseName}.data.value` as const)}
            placeholder="Write content..."
            className="h-32 w-full rounded-2xl border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
          />
        );
    }
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="space-y-6 rounded-2xl border border-slate-200 bg-white/80 p-6 shadow-card dark:border-slate-800 dark:bg-slate-900/60"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
          Title
          <input
            {...register('title', { required: true })}
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-900"
          />
        </label>
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
          Category
          <input
            {...register('category', { required: true })}
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-900"
          />
        </label>
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
          Nav Group
          <input
            {...register('navGroup')}
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-900"
          />
        </label>
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
          Tags (comma separated)
          <input
            {...register('tagsAsString')}
            placeholder="api, sdk, onboarding"
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-900"
          />
        </label>
        <label className="flex items-center gap-3 text-sm font-medium text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            {...register('published')}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          Visible to readers
        </label>
      </div>

      <label className="block text-sm font-medium text-slate-600 dark:text-slate-300">
        Description
        <textarea
          {...register('description')}
          rows={3}
          className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900"
        />
      </label>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-lg font-semibold text-slate-900 dark:text-white">Sections</p>
          <button
            type="button"
            className="rounded-full bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow"
            onClick={() => append(createSectionTemplate())}
          >
            Add section
          </button>
        </div>
        {fields.map((field, idx) => {
          const section = sections[idx];
          return (
            <div key={field.id} className="space-y-4 rounded-2xl border border-slate-200 p-4 dark:border-slate-800">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-700 dark:text-slate-200">Section {idx + 1}</p>
                {fields.length > 1 ? (
                  <button type="button" className="text-sm text-rose-500" onClick={() => remove(idx)}>
                    Remove
                  </button>
                ) : null}
              </div>
              <input
                {...register(`sections.${idx}.title` as const, { required: true })}
                placeholder="Section title"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
              />
              <div className="space-y-3">
                {section?.blocks?.map((block, blockIdx) => (
                  <div
                    key={`${field.id}-block-${blockIdx}`}
                    className="rounded-2xl border border-slate-200 p-3 dark:border-slate-700"
                  >
                    <div className="flex items-center gap-3">
                      <select
                        value={block?.type || 'text'}
                        onChange={(event) =>
                          handleBlockTypeChange(idx, blockIdx, event.target.value as ContentBlock['type'])
                        }
                        className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                      >
                        {['text', 'quote', 'callout', 'image', 'video', 'code', 'list', 'table'].map((type) => (
                          <option key={type} value={type}>
                            {type.toUpperCase()}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="text-xs font-semibold text-rose-500"
                        onClick={() => removeBlock(idx, blockIdx)}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="mt-3">{renderBlockFields(idx, blockIdx, block?.type || 'text')}</div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="rounded-full border border-dashed border-slate-300 px-4 py-2 text-sm text-slate-600 dark:border-slate-600 dark:text-slate-300"
                onClick={() => addBlock(idx)}
              >
                Add content block
              </button>
            </div>
          );
        })}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-2xl bg-brand-600 py-3 text-center text-lg font-semibold text-white transition hover:bg-brand-500 disabled:cursor-not-allowed disabled:bg-slate-400"
      >
        {loading ? 'Saving...' : 'Save documentation'}
      </button>
    </form>
  );
};

export default DocForm;

